import React from 'react';
import { Modal } from 'antd';

export const FULL_IMPORT_VALIDATE_PHASES = [
  { id: 'upload', label: 'Загрузка архива', hint: 'Передача файла на сервер' },
  { id: 'validate', label: 'Проверка структуры', hint: 'Формат tar.gz и database.sql' },
  { id: 'analyze', label: 'Анализ содержимого', hint: 'Файлы, размер и предупреждения' },
];

export const buildProxyBackupImportPhases = (syncToKraken = true) => {
  const phases = [
    { id: 'upload', label: 'Загрузка файла', hint: 'Передача Excel на сервер' },
    { id: 'parse', label: 'Чтение Excel', hint: 'Листы «Прокси», «Клиенты», «Логины ЛК», «Подключения», «Серверы»' },
    { id: 'connections', label: 'Клиенты, подключения и серверы', hint: 'Восстановление записей в БД' },
    { id: 'proxies', label: 'Импорт прокси', hint: 'Создание и обновление записей' },
  ];
  if (syncToKraken) {
    phases.push({
      id: 'kraken',
      label: 'Отправка в Kraken',
      hint: 'Синхронизация прокси и настроек модемов',
    });
  }
  phases.push({ id: 'finish', label: 'Завершение', hint: 'Финализация импорта' });
  return phases;
};

export const buildFullImportRestorePhases = (includeSystemConfigs = false) => {
  const phases = [
    { id: 'upload', label: 'Загрузка архива', hint: 'Передача файла на сервер' },
    { id: 'extract', label: 'Распаковка', hint: 'Извлечение файлов из tar.gz' },
    { id: 'database', label: 'PostgreSQL', hint: 'Восстановление базы данных' },
    { id: 'files', label: 'Файлы проекта', hint: 'Код, конфиги и сборки' },
  ];
  if (includeSystemConfigs) {
    phases.push({ id: 'system', label: 'Systemd и nginx', hint: 'Unit-файлы и vhost-конфиги' });
  }
  phases.push({ id: 'finish', label: 'Завершение', hint: 'Финализация восстановления' });
  return phases;
};

/** Один шаг прогресса для restore из списка/SSH (без ложных галочек по этапам). */
export const buildFullRestoreStorageRestorePhases = (isSshSource = false) => ([
  {
    id: 'restore',
    label: isSshSource ? 'Восстановление с SSH' : 'Восстановление бэкапа',
    hint: isSshSource
      ? 'Скачивание, распаковка, PostgreSQL и файлы проекта (5–15 мин)'
      : 'Распаковка, PostgreSQL и файлы проекта',
  },
]);

/** Один шаг после загрузки архива с диска. */
export const buildFullRestoreUploadRestorePhases = () => ([
  {
    id: 'restore',
    label: 'Восстановление бэкапа',
    hint: 'Распаковка, PostgreSQL и файлы проекта',
  },
]);

export const createFullImportProgressState = (phases, title, subtitle) => ({
  open: true,
  title,
  subtitle,
  startedAt: Date.now(),
  elapsedLabel: null,
  phases: phases.map((phase, index) => ({
    ...phase,
    status: index === 0 ? 'active' : 'pending',
  })),
  percent: 2,
});

export const applyFullImportUploadProgress = (prev, event, uploadShare = 0.38) => {
  if (!prev || !event?.total) {
    return prev;
  }

  const ratio = event.loaded / event.total;
  const uploadPercent = Math.round(ratio * uploadShare * 100);
  const capped = Math.min(Math.round(uploadShare * 100), Math.max(prev.percent, uploadPercent));

  return {
    ...prev,
    percent: capped,
    phases: prev.phases.map((phase, index) => {
      if (index !== 0) {
        return phase;
      }
      if (ratio >= 1) {
        return { ...phase, status: 'done' };
      }
      return { ...phase, status: 'active' };
    }).map((phase, index) => {
      if (ratio >= 1 && index === 1 && phase.status === 'pending') {
        return { ...phase, status: 'active' };
      }
      return phase;
    }),
  };
};

export const advanceFullImportServerPhases = (prev, activeIndex, percent) => {
  if (!prev) {
    return prev;
  }

  return {
    ...prev,
    percent: Math.max(prev.percent, percent),
    phases: prev.phases.map((phase, index) => {
      if (index < activeIndex) {
        return { ...phase, status: 'done' };
      }
      if (index === activeIndex) {
        return { ...phase, status: 'active' };
      }
      return { ...phase, status: 'pending' };
    }),
  };
};

export const completeFullImportProgressState = (prev) => {
  if (!prev) {
    return prev;
  }

  return {
    ...prev,
    percent: 100,
    phases: prev.phases.map((phase) => ({ ...phase, status: 'done' })),
  };
};

/** Медленно поднимать percent, пока ждём долгий API (без ложной смены шагов). */
export const tickFullImportWaitingProgress = (prev, maxPercent = 88) => {
  if (!prev) {
    return prev;
  }

  const startedAt = prev.startedAt || Date.now();
  const elapsedSec = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
  const minutes = Math.floor(elapsedSec / 60);
  const seconds = elapsedSec % 60;
  const elapsedLabel = minutes > 0
    ? `Прошло ${minutes} мин ${seconds} сек`
    : `Прошло ${seconds} сек`;

  return {
    ...prev,
    startedAt,
    elapsedLabel,
    percent: Math.min(maxPercent, Math.max(prev.percent, prev.percent + 1)),
  };
};

const PhaseIcon = ({ status }) => {
  if (status === 'done') {
    return (
      <span className="sett-import-progress__step-icon sett-import-progress__step-icon--done" aria-hidden="true">
        ✓
      </span>
    );
  }

  if (status === 'active') {
    return (
      <span className="sett-import-progress__step-icon sett-import-progress__step-icon--active" aria-hidden="true">
        <span className="sett-import-progress__spinner" />
      </span>
    );
  }

  return (
    <span className="sett-import-progress__step-icon sett-import-progress__step-icon--pending" aria-hidden="true">
      •
    </span>
  );
};

const FullImportProgressModal = ({
  open,
  title,
  subtitle,
  elapsedLabel,
  phases = [],
  percent = 0,
}) => {
  const activePhase = phases.find((phase) => phase.status === 'active');
  const safePercent = Math.max(0, Math.min(100, Math.round(percent)));
  const showStepList = phases.length > 1;

  return (
    <Modal
      open={open}
      footer={null}
      closable={false}
      maskClosable={false}
      keyboard={false}
      centered
      width={520}
      zIndex={1100}
      className="sett-import-progress-modal"
      title={null}
    >
      <div className="sett-import-progress" role="status" aria-live="polite" aria-busy="true">
        <div className="sett-import-progress__header">
          <h3 className="sett-import-progress__title">{title}</h3>
          {subtitle && <p className="sett-import-progress__subtitle">{subtitle}</p>}
          {elapsedLabel && <p className="sett-import-progress__elapsed">{elapsedLabel}</p>}
        </div>

        <div className="sett-import-progress__bar-wrap">
          <div className="sett-import-progress__bar-meta">
            <span>{activePhase?.label || phases[0]?.label || 'Обработка...'}</span>
            <span>{safePercent}%</span>
          </div>
          <div className="sett-import-progress__bar-track" aria-hidden="true">
            <div
              className="sett-import-progress__bar-fill"
              style={{ width: `${safePercent}%` }}
            />
          </div>
        </div>

        {showStepList ? (
          <ol className="sett-import-progress__steps">
            {phases.map((phase) => (
              <li
                key={phase.id}
                className={`sett-import-progress__step sett-import-progress__step--${phase.status}`}
              >
                <PhaseIcon status={phase.status} />
                <div className="sett-import-progress__step-body">
                  <span className="sett-import-progress__step-label">{phase.label}</span>
                  {phase.hint && (
                    <span className="sett-import-progress__step-hint">{phase.hint}</span>
                  )}
                </div>
              </li>
            ))}
          </ol>
        ) : (
          phases[0]?.hint && (
            <p className="sett-import-progress__single-hint">{phases[0].hint}</p>
          )
        )}

        <p className="sett-import-progress__note">Не закрывайте страницу до завершения операции.</p>
      </div>
    </Modal>
  );
};

export default FullImportProgressModal;
